import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AntDesign, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import apis, { api } from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

interface HRDashboardProps {
  onTabChange?: (tab: 'employees' | 'leave' | 'attendance' | 'payroll' | 'performance') => void;
}

const HRDashboard = ({ onTabChange }: HRDashboardProps = {}) => {
  const navigation = useNavigation();
  const theme = themeSelector();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);

  const { data: dashboardData, isLoading, refetch, error } = useQuery({
    queryKey: ['hrDashboard', currentCompany?._id],
    queryFn: async () => {
      try {
        const response = await apis.getHRDashboard();
        console.log('📊 HR Dashboard API Response:', {
          response,
          hasSuccess: response?.success,
          hasData: !!response?.data,
          data: response?.data
        });
        
        // Ensure response structure is correct
        if (response?.success && response?.data) {
          return response.data;
        }
        // Fallback if response structure is different
        const data = response?.data || response || {};
        console.log('📊 HR Dashboard Data:', data);
        return data;
      } catch (err) {
        console.error('❌ HR Dashboard API Error:', err);
        throw err;
      }
    },
    enabled: !!currentCompany?._id,
    refetchOnWindowFocus: true,
    staleTime: 30000, // Refetch every 30 seconds
    retry: 2
  });

  // Debug logging
  React.useEffect(() => {
    if (dashboardData) {
      console.log('📊 HR Dashboard Data Received:', {
        totalEmployees: dashboardData?.totalEmployees,
        activeEmployees: dashboardData?.activeEmployees,
        departmentsCount: dashboardData?.departmentsCount,
        pendingLeaveRequests: dashboardData?.pendingLeaveRequests,
        todayAttendance: dashboardData?.todayAttendance,
        fullData: dashboardData
      });
    }
    if (error) {
      console.error('❌ HR Dashboard Error:', error);
    }
  }, [dashboardData, error]);

  const handleNavigate = (screen: string) => {
    if (onTabChange) {
      // Map screen names to tab keys
      const screenToTabMap: { [key: string]: 'employees' | 'leave' | 'attendance' | 'payroll' | 'performance' } = {
        'EmployeeManagement': 'employees',
        'LeaveManagement': 'leave',
        'AttendanceManagement': 'attendance',
        'PayrollManagement': 'payroll',
        'PerformanceReviews': 'performance',
      };
      const tab = screenToTabMap[screen];
      if (tab) {
        onTabChange(tab);
      }
    } else {
      navigation.navigate(screen as any);
    }
  };

  const stats = [
    {
      id: 'employees',
      label: 'Total Employees',
      value: dashboardData?.totalEmployees || 0,
      icon: 'users',
      color: '#3b82f6',
      screen: 'EmployeeManagement'
    },
    {
      id: 'active',
      label: 'Active Employees',
      value: dashboardData?.activeEmployees || 0,
      icon: 'user-check',
      color: '#10b981',
      screen: 'EmployeeManagement'
    },
    {
      id: 'departments',
      label: 'Departments',
      value: dashboardData?.departmentsCount || 0,
      icon: 'building',
      color: '#f59e0b',
      screen: 'EmployeeManagement'
    },
    {
      id: 'pendingLeave',
      label: 'Pending Leave',
      value: dashboardData?.pendingLeaveRequests || 0,
      icon: 'calendar-times',
      color: '#ef4444',
      screen: 'LeaveManagement'
    },
    {
      id: 'attendance',
      label: 'Today Attendance',
      value: dashboardData?.todayAttendance || 0,
      icon: 'clock',
      color: '#8b5cf6',
      screen: 'AttendanceManagement'
    }
  ];

  const quickActions = [
    {
      id: 'employees',
      label: 'Manage Employees',
      icon: 'users',
      screen: 'EmployeeManagement',
      color: '#3b82f6'
    },
    {
      id: 'leave',
      label: 'Leave Requests',
      icon: 'calendar-times',
      screen: 'LeaveManagement',
      color: '#10b981'
    },
    {
      id: 'attendance',
      label: 'Attendance',
      icon: 'clock',
      screen: 'AttendanceManagement',
      color: '#8b5cf6'
    },
    {
      id: 'payroll',
      label: 'Payroll',
      icon: 'dollar-sign',
      screen: 'PayrollManagement',
      color: '#f59e0b'
    },
    {
      id: 'performance',
      label: 'Performance Reviews',
      icon: 'star',
      screen: 'PerformanceReviews',
      color: '#ec4899'
    }
  ];

  return (
    <DefaultScreen>
      <View style={styles.container}>
        <View style={styles.header}>
          <Title style={{ fontSize: 28, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
            HR Dashboard
          </Title>
          <Text style={{ fontSize: 14, color: theme ? '#6b7280' : '#9ca3af', marginTop: 4 }}>
            Manage your human resources
          </Text>
        </View>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Text>Loading...</Text>
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <Text style={{ color: theme ? '#ef4444' : '#f87171', marginBottom: 8 }}>
              Error loading dashboard data
            </Text>
            <TouchableOpacity
              style={[styles.retryButton, { backgroundColor: '#3b82f6' }]}
              onPress={() => refetch()}
            >
              <Text style={{ color: '#ffffff', fontWeight: '600' }}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Statistics Cards */}
            <View style={styles.statsContainer}>
              {stats.map((stat) => (
                <TouchableOpacity
                  key={stat.id}
                  style={[
                    styles.statCard,
                    {
                      backgroundColor: theme ? '#ffffff' : '#1f2937',
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 3
                    }
                  ]}
                  onPress={() => handleNavigate(stat.screen)}
                >
                  <View style={[styles.statIconContainer, { backgroundColor: `${stat.color}20` }]}>
                    <FontAwesome5 name={stat.icon as any} size={24} color={stat.color} />
                  </View>
                  <Text style={[styles.statValue, { color: theme ? '#111827' : '#f9fafb' }]}>
                    {stat.value}
                  </Text>
                  <Text style={[styles.statLabel, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                    {stat.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Quick Actions */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: theme ? '#111827' : '#f9fafb' }]}>
                Quick Actions
              </Text>
              <View style={styles.actionsGrid}>
                {quickActions.map((action) => (
                  <TouchableOpacity
                    key={action.id}
                    style={[
                      styles.actionCard,
                      {
                        backgroundColor: theme ? '#ffffff' : '#1f2937',
                        borderColor: theme ? '#e5e7eb' : '#374151'
                      }
                    ]}
                    onPress={() => handleNavigate(action.screen)}
                  >
                    <View style={[styles.actionIconContainer, { backgroundColor: `${action.color}20` }]}>
                      <FontAwesome5 name={action.icon as any} size={20} color={action.color} />
                    </View>
                    <Text style={[styles.actionLabel, { color: theme ? '#111827' : '#f9fafb' }]}>
                      {action.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Recent Activities */}
            {dashboardData?.recentActivities && dashboardData.recentActivities.length > 0 && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: theme ? '#111827' : '#f9fafb' }]}>
                  Recent Activities
                </Text>
                <View style={[
                  styles.activitiesContainer,
                  { backgroundColor: theme ? '#ffffff' : '#1f2937' }
                ]}>
                  {dashboardData.recentActivities.slice(0, 5).map((activity: any, index: number) => (
                    <View key={index} style={styles.activityItem}>
                      <View style={styles.activityIcon}>
                        <MaterialCommunityIcons
                          name="circle"
                          size={8}
                          color={theme ? '#3b82f6' : '#60a5fa'}
                        />
                      </View>
                      <View style={styles.activityContent}>
                        <Text style={[styles.activityText, { color: theme ? '#111827' : '#f9fafb' }]}>
                          {activity.type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Text>
                        <Text style={[styles.activityTime, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                          {new Date(activity.timestamp).toLocaleString()}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20
  },
  header: {
    marginBottom: 24
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 12
  },
  statsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24
  },
  statCard: {
    width: Platform.OS === 'web' ? '48%' : '48%',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12
  },
  statIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 4
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '500'
  },
  section: {
    marginBottom: 24
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 16
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12
  },
  actionCard: {
    width: Platform.OS === 'web' ? '31%' : '48%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    marginBottom: 12
  },
  actionIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8
  },
  actionLabel: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center'
  },
  activitiesContainer: {
    borderRadius: 12,
    padding: 16
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb'
  },
  activityIcon: {
    marginRight: 12
  },
  activityContent: {
    flex: 1
  },
  activityText: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4
  },
  activityTime: {
    fontSize: 12
  }
});

export default HRDashboard;

